import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy, Tooltip } from 'vnetwork-ui'
import { ChevronDown, ChevronUp } from 'lucide-react'

interface LeadConversionFlowCompactProps {
    currentStatus?: string | null
    isConverted?: boolean
    hasAssignees?: boolean
    hideTitle?: boolean
}

// Map contact status to flow stage id
const getActiveStageId = (
    status: string | null | undefined,
    isConverted: boolean,
    hasAssignees: boolean
): string => {
    if (isConverted) return 'customer'

    if (hasAssignees) {
        switch (status) {
            case 'warm-lead':
                return 'warm_lead'
            case 'sale-qualified-lead':
                return 'sql'
            case 'cool-lead':
                return 'cool_lead'
            case 'un-qualified-lead':
                return 'unqualified'
            case 'MATCHED':
                return 'company_contact'
            default:
                return 'assigned'
        }
    }

    if (!status || status === 'UNVERIFIED' || status === 'UN-MATCHED') {
        return 'collect_lead'
    }

    switch (status) {
        case 'FOLLOWING':
            return 'assigned'
        case 'warm-lead':
            return 'warm_lead'
        case 'sale-qualified-lead':
            return 'sql'
        case 'cool-lead':
            return 'cool_lead'
        case 'un-qualified-lead':
            return 'unqualified'
        case 'MATCHED':
            return 'company_contact'
        default:
            return 'collect_lead'
    }
}

const LeadConversionFlowCompact: React.FC<LeadConversionFlowCompactProps> = ({
    currentStatus,
    isConverted = false,
    hasAssignees = false,
    hideTitle = false,
}) => {
    const { t } = useTranslation('marketing')
    const [showLegend, setShowLegend] = useState(false)
    const activeStageId = getActiveStageId(
        currentStatus,
        isConverted,
        hasAssignees
    )

    // Check if a stage is active
    const isActive = (stageId: string) => stageId === activeStageId

    // Check if a stage should be disabled (crossed out)
    // When one classification is active, others are disabled
    const classificationStages = [
        'warm_lead',
        'sql',
        'cool_lead',
        'unqualified',
    ]
    const isDisabled = (stageId: string) => {
        if (classificationStages.includes(activeStageId)) {
            return (
                classificationStages.includes(stageId) &&
                stageId !== activeStageId
            )
        }
        return false
    }

    // Box component
    const Box = ({
        id,
        label,
        bgColor,
        borderColor,
        textColor,
        description,
    }: {
        id: string
        label: string
        bgColor: string
        borderColor: string
        textColor: string
        description?: string
    }) => {
        const disabled = isDisabled(id)
        const active = isActive(id)

        const content = (
            <div
                className={`px-3 py-1.5 rounded-md text-xs font-medium text-center transition-all relative ${
                    active
                        ? 'ring-2 ring-blue-500 ring-offset-2 shadow-md scale-105 cursor-pointer'
                        : disabled
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:shadow-sm cursor-pointer'
                }`}
                style={{
                    backgroundColor: disabled ? '#F3F4F6' : bgColor,
                    border: `1px solid ${disabled ? '#D1D5DB' : borderColor}`,
                    color: disabled ? '#9CA3AF' : textColor,
                }}
            >
                <span className={disabled ? 'line-through' : ''}>{label}</span>
            </div>
        )

        // Only show a tooltip when a description is provided.
        // Classification boxes (warm/sql/cool/unqualified) pass no description → no tooltip.
        return description ? (
            <Tooltip title={description}>{content}</Tooltip>
        ) : (
            content
        )
    }

    // Vertical arrow between steps
    const DownArrow = () => (
        <div className="flex justify-center py-0.5">
            <svg
                width="12"
                height="16"
                viewBox="0 0 12 16"
                className="text-gray-300"
            >
                <path
                    d="M6 0v12M1 9l5 5 5-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    )

    // Legend card component
    const LegendCard = ({
        title,
        borderColor,
        items,
        example,
    }: {
        title: string
        borderColor: string
        items: { label: string; highlight?: boolean; strikethrough?: boolean }[]
        example?: string
    }) => (
        <div
            className="bg-white rounded p-2.5 text-[11px]"
            style={{ borderLeft: `3px solid ${borderColor}` }}
        >
            <div className="font-semibold text-gray-800 mb-1">{title}</div>
            <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                {items.map((item, idx) => (
                    <li key={idx}>
                        {item.strikethrough ? (
                            <span className="text-red-500 line-through italic">
                                {item.label}
                            </span>
                        ) : item.highlight ? (
                            <span className="text-red-500">{item.label}</span>
                        ) : (
                            item.label
                        )}
                    </li>
                ))}
            </ul>
            {example && (
                <div className="mt-1 text-gray-500 italic text-[10px]">
                    {t('lead_flow.example')}: {example}
                </div>
            )}
        </div>
    )

    const flowContent = (
        <div className="px-3 py-2">
            {/* Vertical Flow */}
            <div className="flex flex-col items-stretch">
                <Box
                    id="marketing_campaign"
                    label={t('lead_flow.marketing_campaign')}
                    bgColor="#FFFFFF"
                    borderColor="#E5E7EB"
                    textColor="#374151"
                    description={t('lead_flow.marketing_campaign_desc')}
                />
                <DownArrow />
                <Box
                    id="collect_lead"
                    label={t('lead_flow.collect_lead')}
                    bgColor="#F3F4F6"
                    borderColor="#D1D5DB"
                    textColor="#374151"
                    description={t('lead_flow.collect_lead_desc')}
                />
                <DownArrow />
                <Box
                    id="assigned"
                    label={t('lead_flow.assigned')}
                    bgColor="#DBEAFE"
                    borderColor="#60A5FA"
                    textColor="#1D4ED8"
                    description={t('lead_flow.assigned_desc')}
                />
                <DownArrow />

                {/* Classification branch — grid 2x2 */}
                <div className="grid grid-cols-2 gap-1.5">
                    <Box
                        id="warm_lead"
                        label={t('lead_flow.warm_lead')}
                        bgColor="#FEF3C7"
                        borderColor="#F59E0B"
                        textColor="#B45309"
                    />
                    <Box
                        id="sql"
                        label={t('lead_flow.sql')}
                        bgColor="#D1FAE5"
                        borderColor="#34D399"
                        textColor="#059669"
                    />
                    <Box
                        id="cool_lead"
                        label={t('lead_flow.cool_lead')}
                        bgColor="#F3F4F6"
                        borderColor="#D1D5DB"
                        textColor="#374151"
                    />
                    <Box
                        id="unqualified"
                        label={t('lead_flow.unqualified')}
                        bgColor="#FEE2E2"
                        borderColor="#F87171"
                        textColor="#DC2626"
                    />
                </div>

                <DownArrow />
                <Box
                    id="company_contact"
                    label={t('lead_flow.company_contact')}
                    bgColor="#D1FAE5"
                    borderColor="#34D399"
                    textColor="#059669"
                    description={t('lead_flow.company_contact_desc')}
                />
            </div>

            {/* Legend Cards - Collapsible */}
            <div className="mt-3 pt-3 border-t border-gray-200">
                <div
                    className="flex items-center justify-between cursor-pointer mb-2"
                    onClick={() => setShowLegend(!showLegend)}
                >
                    <Typogaphy className="text-xs font-semibold text-gray-600">
                        {t('lead_flow.legend_title')}
                    </Typogaphy>
                    <button className="p-0.5 hover:bg-gray-200 rounded">
                        {showLegend ? (
                            <ChevronUp size={14} className="text-gray-500" />
                        ) : (
                            <ChevronDown size={14} className="text-gray-500" />
                        )}
                    </button>
                </div>
                {showLegend && (
                    <div className="flex flex-col gap-2">
                        <LegendCard
                            title={t('lead_flow.legend.cool_lead')}
                            borderColor="#D1D5DB"
                            items={[
                                { label: t('lead_flow.legend.budget_yes') },
                                { label: t('lead_flow.legend.authority_yes') },
                                {
                                    label: t('lead_flow.legend.need_not_yet'),
                                    highlight: true,
                                },
                            ]}
                            example={t('lead_flow.legend.cool_lead_example')}
                        />
                        <LegendCard
                            title={t('lead_flow.legend.warm_lead')}
                            borderColor="#F59E0B"
                            items={[
                                { label: t('lead_flow.legend.authority_yes') },
                                {
                                    label: t(
                                        'lead_flow.legend.need_interested'
                                    ),
                                    highlight: true,
                                },
                            ]}
                            example={t('lead_flow.legend.warm_lead_example')}
                        />
                        <LegendCard
                            title={t('lead_flow.legend.sql')}
                            borderColor="#34D399"
                            items={[
                                { label: t('lead_flow.legend.budget_yes') },
                                { label: t('lead_flow.legend.authority_yes') },
                                {
                                    label: t('lead_flow.legend.need_has'),
                                    highlight: true,
                                },
                                { label: t('lead_flow.legend.timeline_has') },
                            ]}
                            example={t('lead_flow.legend.sql_example')}
                        />
                        <LegendCard
                            title={t('lead_flow.legend.unqualified')}
                            borderColor="#F87171"
                            items={[
                                {
                                    label: t('lead_flow.legend.budget_no'),
                                    highlight: true,
                                },
                                {
                                    label: t('lead_flow.legend.authority_no'),
                                    highlight: true,
                                },
                                {
                                    label: t('lead_flow.legend.need_no'),
                                    highlight: true,
                                },
                            ]}
                            example={t('lead_flow.legend.unqualified_example')}
                        />
                    </div>
                )}
            </div>
        </div>
    )

    if (hideTitle) {
        return flowContent
    }

    return (
        <div className="bg-gray-50 border border-gray-200 rounded-md mb-4">
            <div className="flex items-center justify-between px-4 py-2">
                <Typogaphy className="text-xs font-semibold text-gray-600">
                    {t('lead_flow.title')}
                </Typogaphy>
            </div>
            {flowContent}
        </div>
    )
}

export default LeadConversionFlowCompact
